import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { SEOWebPage } from '@/components/base/SEOSchema';
import { comics, categories, bannerSlides, siteInfo } from '@/mocks/comics';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const bannerTimer = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    bannerTimer.current = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => { if (bannerTimer.current) clearInterval(bannerTimer.current); };
  }, []);

  const hotComics = comics.filter((c) => c.rating >= 4.7).slice(0, 8);
  const latestComics = [...comics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 12);
  const completedComics = comics.filter((c) => c.status === '已完结').slice(0, 4);

  return (
    <>
      <SEOWebPage
        title="洞洞杂货铺漫画-韩漫全集免费阅读-51漫画网"
        description={siteInfo.description}
        url="https://ytzshn.cn"
        breadcrumbItems={[{ name: '首页', url: 'https://ytzshn.cn' }]}
      />

      <Navbar />

      <main>
        {/* Hero Banner */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden" aria-label="热门推荐横幅">
          {bannerSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${index === currentBanner ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
              <img src={slide.cover} alt={slide.title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="max-w-xl">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-medium mb-4">
                  {bannerSlides[currentBanner].subtitle}
                </span>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  {bannerSlides[currentBanner].title}
                </h1>
                <p className="text-white/80 text-sm md:text-base mb-6 leading-relaxed">
                  {bannerSlides[currentBanner].description}
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/article/${bannerSlides[currentBanner].comicId}`}
                    className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    立即阅读
                  </Link>
                  <Link
                    to={`/category/romance`}
                    className="px-6 py-3 rounded-full bg-white/20 text-white text-sm font-medium hover:bg-white/30 backdrop-blur-sm transition-all whitespace-nowrap cursor-pointer"
                  >
                    浏览更多
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Banner dots + arrows */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {bannerSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentBanner(i)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === currentBanner ? 'w-6 bg-primary-500' : 'bg-white/50 hover:bg-white/80'}`}
                aria-label={`切换到第${i + 1}张横幅`}
              />
            ))}
          </div>

          <button
            onClick={() => setCurrentBanner((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length)}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button
            onClick={() => setCurrentBanner((prev) => (prev + 1) % bannerSlides.length)}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </section>

        {/* Platform Stats */}
        <section className="py-8 bg-background-50 border-b border-background-200/70" aria-label="平台数据">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: 'ri-book-read-line', value: `${siteInfo.stats.totalComics}+`, label: '收录漫画' },
                { icon: 'ri-pages-line', value: `${(siteInfo.stats.totalChapters / 10000).toFixed(0)}万+`, label: '漫画章节' },
                { icon: 'ri-user-heart-line', value: `${(siteInfo.stats.totalUsers / 10000).toFixed(0)}万+`, label: '注册用户' },
                { icon: 'ri-timer-flash-line', value: `${siteInfo.stats.dailyUpdate}+`, label: '每日更新' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className={`${stat.icon} text-lg text-primary-600`}></i>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-foreground-900">{stat.value}</p>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 bg-background-50" aria-label="漫画分类导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">分类浏览</h2>
              <Link to="/category/romance" className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors">
                    <i className="ri-bookmark-line text-lg text-foreground-600 group-hover:text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{cat.name}</p>
                    <p className="text-xs text-foreground-500">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-12 bg-background-100" aria-label="热门推荐">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">热门推荐</h2>
                <p className="text-sm text-foreground-500 mt-1">全网评分最高的韩漫作品</p>
              </div>
              <Link to="/category/romance" className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap">
                更多推荐 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-12 bg-background-50" aria-label="最新更新">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">最新更新</h2>
                <p className="text-sm text-foreground-500 mt-1">实时追踪最新漫画章节</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="small" />
              ))}
            </div>
          </div>
        </section>

        {/* Completed Works */}
        <section className="py-12 bg-background-100" aria-label="已完结佳作">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">已完结佳作</h2>
                <p className="text-sm text-foreground-500 mt-1">一口气看到结局的完美体验</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {completedComics.map((comic) => (
                <Link key={comic.id} to={`/article/${comic.id}`} className="group flex gap-4 p-4 rounded-xl bg-background-50 hover:bg-primary-50/50 transition-colors cursor-pointer">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} 封面`}
                    title={`${comic.title} - 已完结韩漫`}
                    className="w-[80px] h-[108px] object-cover object-top rounded-lg flex-shrink-0"
                  />
                  <div className="flex flex-col justify-between min-w-0">
                    <div>
                      <h3 className="font-semibold text-sm text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">共{comic.totalChapters}话</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs font-semibold text-foreground-700">{comic.rating}</span>
                      <span className="text-xs text-foreground-400 ml-2">{comic.views.toLocaleString()} 次阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 overflow-hidden" aria-label="开始阅读">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20reading%20nook%20with%20soft%20ambient%20lighting%2C%20bookshelves%20filled%20with%20colorful%20books%20and%20comics%2C%20a%20cozy%20armchair%2C%20gentle%20warm%20tones%2C%20atmospheric%20and%20inviting%20mood%2C%20Korean%20manhwa%20inspired%20color%20palette&width=1600&height=400&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              立即开启您的韩漫阅读之旅
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              数百部精选韩漫，每日更新，全部免费。洞洞杂货铺等热门作品等你来追！
            </p>
            <Link
              to="/category/romance"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-rocket-line"></i>
              立刻开始免费阅读
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}